import { Platform } from 'react-native'

import { useMutation } from '@tanstack/react-query'
import { useToast } from '@zet/common/components/toast'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { IRequestData } from '@zet/customer/src/api/functions/customerAdditionStateMachine'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { webStates } from '@zet/customer/src/utils/addCustomerStates'
import { isIOS, isWebView } from '@zet/integration-flow/utils'
import { i18n } from '@zet/localization'
import { zetApi } from '@zet/network'
import { replaceLoginHash } from '@zet/utils/loginHash'

import { useFontFlowStore } from '../../store/fontFlowStore'
import { getFormatRequestData } from '../utils/getFormatRequestData'

const getAddCustomerNextState = async (requestData: IRequestData) => {
  const requestBody = getFormatRequestData(requestData)
  const { data } = await zetApi.post('customer/onboard', requestBody, {
    headers: {
      ...zetApi.defaults.headers.common,
      appOs: !isWebView() ? 'web' : isIOS() ? 'ios' : 'android',
    },
  })
  if (data?.data) {
    return data.data
  } else {
    return data?.error
  }
}

export const useAddCustomerState = () => {
  const {
    setCustomerId,
    customerId: addCustomerId,
    variantId,
  } = useAddCustomerStore((store) => store)
  const navigation = useNavigation()
  const isJfsFlow = useFontFlowStore((state) => state.isJfsFlow)
  const toast = useToast()
  return useMutation(getAddCustomerNextState, {
    onSuccess: (data, variables) => {
      if (data?.actionData?.hash) {
        replaceLoginHash(data?.actionData?.hash)
        const decodedHash = decodeURIComponent(data?.actionData?.hash)
        if (typeof window !== 'undefined' && isJfsFlow) {
          const url = new URL(window.location.href)
          url.searchParams.set('hash', decodedHash)
          window.history.replaceState({}, '', url.toString())
        }
      }
      if (data?.actionData?.error?.code === 2025) {
        toast?.show({
          intent: 'error',
          heading: i18n.t('toastMessages.error'),
          description:
            data?.actionData?.error?.message ||
            i18n.t('toastMessages.somethingWentWrong'),
          hideAfter: 3000,
        })
      }

      if (data?.code === 400) {
        toast?.show({
          intent: 'error',
          heading: i18n.t('toastMessages.error'),
          description:
            data?.message || i18n.t('toastMessages.somethingWentWrong'),
          hideAfter: 3000,
        })
        return
      }
      const { lockedTill, customerId, reason } =
        // eslint-disable-next-line no-prototype-builtins
        data.hasOwnProperty('actionData') && data.actionData

      if (data?.lockedTill) {
        toast?.show({
          intent: 'error',
          heading: i18n.t('toastMessages.error'),
          description: data?.reason,
          hideAfter: 3000,
        })
      }

      if (lockedTill) {
        toast?.show({
          intent: 'error',
          heading: i18n.t('toastMessages.error'),
          description: reason,
          hideAfter: 3000,
        })
        return data?.actionData
      }

      if (data?.action) {
        if (data?.actionData?.customerId) {
          setCustomerId(data?.actionData?.customerId)
        }

        const navigationParams: Record<string, any> = {
          customerId: customerId || addCustomerId,
          customerName: variables.fullName,
          customerPhoneNumber: variables.phoneNumber,
          ...(variantId && { variantId }),
          ...(variantId && { categoryId: 15 }),
        }

        // Only pass isJfsFlow as a param on web to avoid affecting mobile flow
        if (Platform.OS === 'web') {
          navigationParams.isJfsFlow = isJfsFlow ?? false
        }

        navigation.navigate(webStates[data?.action], navigationParams)
      }
    },
    onError: (error: any) => {
      toast?.show({
        intent: 'error',
        heading: i18n.t('toastMessages.error'),
        description:
          error?.response?.data?.error?.message ||
          i18n.t('toastMessages.somethingWentWrong'),
        hideAfter: 3000,
      })
    },
  })
}
